import React, { Fragment } from 'react';
import propTypes from 'prop-types';
import capitalize from 'lodash/capitalize';

import { calculatePageContentWeights } from 'lib/contentSize';

/**
 * Calculate percentage
 * @param {number} numerator - top
 * @param {number} denominator - bottom
 * @returns {number} rounded percentage
 */
const calculatePercent = function calculatePercent(numerator, denominator) {
  return Math.round((numerator / denominator) * 100);
};

/**
 * Format size breakdown
 * @param {string} viewport - desktop / mobile
 * @param {number} totalSize - calculated total of images on page
 * @param {object} stats - calculated breakdown of size
 * @returns {Node} react node
 */
const formatSizeBreakdown = function formatSizeBreakdown(viewport, totalSize, stats) {
  return (
    <Fragment>
      {`${viewport} Content Weight: ${totalSize} KB`}
      <br />
      {`Small images (<100 KB): ${calculatePercent(stats.small, stats.count)}%`}
      <br />
      {`Medium images (100–200 KB): ${calculatePercent(stats.medium, stats.count)}%`}
      <br />
      {`Large images (>200 KB): ${calculatePercent(stats.large, stats.count)}%`}
    </Fragment>
  );
};

/**
 * Display image sizes by components
 * @param {object} props - props
 * @returns {Node} react node
 */
const ImageSize = ({ contentSizes }) => {
  const {
    desktopImageSize,
    desktopStats,
    mobileImageSize,
    mobileStats
  } = calculatePageContentWeights(contentSizes);

  if (contentSizes.length) {
    return (
      <div className="p-4">
        {Boolean(desktopImageSize) && (
          <div className="pb-2">
            {formatSizeBreakdown('Desktop', desktopImageSize, desktopStats)}
          </div>
        )}
        {Boolean(mobileImageSize) && (
          <div className="pb-2">
            {formatSizeBreakdown('Mobile', mobileImageSize, mobileStats)}
          </div>
        )}
        {contentSizes.length &&
          contentSizes.map(
            (component, index) =>
              component && (
                <div key={`${component.id}-${index}`} data-test-id="contentWeightResult">
                  {`${index + 1}. ${component.name}`}
                  <div className="pb-2 pl-4">
                    {component.images.map(
                      (image, imageIndex) =>
                        Boolean(image.imageSize) && (
                          <div
                            key={`${component.id}-${index}-${imageIndex}`}
                            data-test-id="contentWeightResult-imageSize"
                          >
                            {`${capitalize(image.context)}: ${Math.round(
                              image.imageSize * 10
                            ) / 10} KB `}
                            <a href={image.imageUrl} target="_blank" rel="noopener noreferrer">
                              View
                            </a>
                          </div>
                        )
                    )}
                  </div>
                </div>
              )
          )}
      </div>
    );
  }

  return <div className="p-4">No images on page</div>;
};

ImageSize.propTypes = {
  contentSizes: propTypes.arrayOf(
    propTypes.shape({
      id: propTypes.string,
      name: propTypes.string,
      images: propTypes.arrayOf(propTypes.shape())
    })
  ).isRequired
};

export default ImageSize;
